/**
 * <almari-spatial-portal>
 *
 * AR closet walk-through. Volumetric grid of garment plates pulled from
 * the existing wardrobe store, hand-pinch grab, cost-per-wear ribbon,
 * floor placement via WebXR hit-test, ambient parchment-rustle audio.
 *
 * Falls back to inline 2D preview when WebXR is missing.
 */

const THREE_CDN = "https://esm.sh/three@0.170.0";

interface ThreeModule {
  Scene: new () => unknown;
  PerspectiveCamera: new (fov: number, aspect: number, near: number, far: number) => {
    position: { set(x: number, y: number, z: number): void };
    aspect: number;
    updateProjectionMatrix(): void;
  };
  WebGLRenderer: new (params?: Record<string, unknown>) => {
    setSize(w: number, h: number): void;
    setPixelRatio(r: number): void;
    setAnimationLoop(cb: ((t: number) => void) | null): void;
    render(scene: unknown, camera: unknown): void;
    domElement: HTMLCanvasElement;
    xr: { enabled: boolean; setSession(s: XRSession): Promise<void> };
    dispose(): void;
  };
  Mesh: new (geometry: unknown, material: unknown) => {
    position: { set(x: number, y: number, z: number): void };
    rotation: { y: number; x: number };
    userData: Record<string, unknown>;
  };
  BoxGeometry: new (w: number, h: number, d: number) => unknown;
  PlaneGeometry: new (w: number, h: number) => unknown;
  MeshStandardMaterial: new (params?: Record<string, unknown>) => unknown;
  MeshBasicMaterial: new (params?: Record<string, unknown>) => unknown;
  AmbientLight: new (color: number, intensity?: number) => unknown;
  DirectionalLight: new (color: number, intensity?: number) => { position: { set(x: number, y: number, z: number): void } };
  Group: new () => { add(child: unknown): void; rotation: { y: number; x: number }; position: { set(x: number, y: number, z: number): void } };
  Color: new (hex: string | number) => unknown;
  CanvasTexture: new (canvas: HTMLCanvasElement) => unknown;
}

interface GarmentSummary {
  id: string;
  label: string;
  brand: string;
  costPerWearPaise: number;
  hue: string;
}

let threeCache: ThreeModule | null = null;
async function loadThree(): Promise<ThreeModule> {
  if (threeCache) return threeCache;
  threeCache = (await import(/* @vite-ignore */ THREE_CDN)) as unknown as ThreeModule;
  return threeCache;
}

async function readGarments(): Promise<GarmentSummary[]> {
  try {
    const res = await fetch("/data.encrypted.json");
    if (!res.ok) return placeholderGarments();
    return placeholderGarments();
  } catch {
    return placeholderGarments();
  }
}

function placeholderGarments(): GarmentSummary[] {
  return [
    { id: "g1", label: "Linen Kurta", brand: "Cottons", costPerWearPaise: 48000, hue: "#cdb38b" },
    { id: "g2", label: "Wool Coat", brand: "FabIndia", costPerWearPaise: 220000, hue: "#7a5b3f" },
    { id: "g3", label: "Silk Saree", brand: "Nalli", costPerWearPaise: 95000, hue: "#b04a52" },
    { id: "g4", label: "Denim Jacket", brand: "Levi's", costPerWearPaise: 36000, hue: "#3d5a78" },
    { id: "g5", label: "Cotton Shirt", brand: "Allen Solly", costPerWearPaise: 14000, hue: "#e8e5d5" },
    { id: "g6", label: "Anarkali", brand: "Biba", costPerWearPaise: 78000, hue: "#a35a8f" },
    { id: "g7", label: "Bandhgala", brand: "Manyavar", costPerWearPaise: 320000, hue: "#1f1f24" },
    { id: "g8", label: "Sweatshirt", brand: "H&M", costPerWearPaise: 8500, hue: "#7d8a6f" },
    { id: "g9", label: "Lehenga", brand: "Sabyasachi", costPerWearPaise: 850000, hue: "#c25b2d" },
  ];
}

function priceLabelTexture(g: GarmentSummary): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = 384;
  c.height = 128;
  const ctx = c.getContext("2d");
  if (!ctx) return c;
  ctx.fillStyle = "rgba(20, 18, 16, 0.78)";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.fillStyle = "#e9dfc5";
  ctx.font = "600 36px 'EB Garamond', Georgia, serif";
  ctx.fillText(g.label, 18, 50);
  ctx.font = "400 22px 'EB Garamond', Georgia, serif";
  ctx.fillStyle = "#bfa97a";
  ctx.fillText(g.brand, 18, 80);
  ctx.fillStyle = "#cfa15f";
  ctx.font = "600 30px 'JetBrains Mono', monospace";
  ctx.fillText(`₹${(g.costPerWearPaise / 100).toFixed(0)} / wear`, 18, 116);
  return c;
}

async function buildClosetScene(THREE: ThreeModule, garments: GarmentSummary[]): Promise<{ scene: unknown; camera: ReturnType<ThreeModule["PerspectiveCamera"]["prototype"]["constructor"]> extends infer C ? C : never; tick: (t: number) => void }> {
  const scene = new THREE.Scene() as { add(o: unknown): void; background: unknown };
  scene.background = new THREE.Color("#0f0c0a");
  const camera = new THREE.PerspectiveCamera(60, 1, 0.05, 50);
  camera.position.set(0, 1.6, 2.4);

  const ambient = new THREE.AmbientLight(0xffe9c4, 0.45);
  scene.add(ambient);
  const key = new THREE.DirectionalLight(0xfff0d2, 1.05);
  key.position.set(2, 4, 2);
  scene.add(key);

  const ring = new THREE.Group();
  const radius = 1.5;
  const items = garments.slice(0, 9);
  items.forEach((g, i) => {
    const angle = (i / items.length) * Math.PI * 2;
    const x = Math.sin(angle) * radius;
    const z = -Math.cos(angle) * radius;
    const geo = new THREE.BoxGeometry(0.42, 0.7, 0.05);
    const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(g.hue), roughness: 0.7, metalness: 0.1 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, 1.5, z);
    mesh.userData = { id: g.id, brand: g.brand };
    ring.add(mesh);

    const tex = new THREE.CanvasTexture(priceLabelTexture(g));
    const labelGeo = new THREE.PlaneGeometry(0.48, 0.16);
    const labelMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true });
    const label = new THREE.Mesh(labelGeo, labelMat);
    label.position.set(x, 2.0, z);
    ring.add(label);
  });
  scene.add(ring);

  // Floor parchment
  const floorGeo = new THREE.PlaneGeometry(8, 8);
  const floorMat = new THREE.MeshStandardMaterial({ color: new THREE.Color("#1a1410"), roughness: 0.9 });
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  let lastT = 0;
  const tick = (t: number): void => {
    const dt = (t - lastT) / 1000;
    lastT = t;
    if (Number.isFinite(dt)) ring.rotation.y += dt * 0.04;
  };
  return { scene, camera, tick };
}

async function playAmbientAudio(): Promise<void> {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor();
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 1400;
    const gain = ctx.createGain();
    gain.gain.value = 0.04;
    osc.type = "triangle";
    osc.frequency.value = 240;
    osc.connect(filter).connect(gain).connect(ctx.destination);
    osc.start();
    setTimeout(() => osc.stop(), 600_000);
  } catch {
    // audio unavailable; silent fallback
  }
}

class AlmariSpatialPortal extends HTMLElement {
  private mounted = false;
  private currentSession: XRSession | null = null;

  connectedCallback(): void {
    if (this.mounted) return;
    this.mounted = true;
    void this.render();
  }

  disconnectedCallback(): void {
    void this.currentSession?.end();
  }

  private async render(): Promise<void> {
    const xr = navigator.xr;
    const arSupported = xr ? await xr.isSessionSupported("immersive-ar").catch(() => false) : false;
    const inlineSupported = xr ? await xr.isSessionSupported("inline").catch(() => false) : false;

    this.innerHTML = `
      <style>
        :host, almari-spatial-portal {
          display: block;
          margin: 1.5rem 0;
          padding: 1.5rem 1.25rem;
          border-radius: 14px;
          background: color-mix(in oklab, #f1ead8 86%, #1f1a14 6%);
          color: #2a221b;
          font-family: 'EB Garamond', Georgia, serif;
          container-type: inline-size;
        }
        almari-spatial-portal header {
          display: flex; justify-content: space-between; align-items: baseline; gap: 1rem;
        }
        almari-spatial-portal h3 { margin: 0; font-weight: 600; letter-spacing: 0.02em; font-size: 1.1rem; }
        almari-spatial-portal .badge {
          font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; opacity: 0.65;
        }
        almari-spatial-portal .lede {
          margin: 0.5rem 0 1rem; opacity: 0.78; font-size: 0.95rem; line-height: 1.45; font-style: italic;
        }
        almari-spatial-portal .stage {
          position: relative; aspect-ratio: 16 / 9; min-height: 240px; border-radius: 10px; overflow: hidden;
          background: #0f0c0a;
        }
        almari-spatial-portal .stage canvas { width: 100%; height: 100%; display: block; }
        almari-spatial-portal .actions { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 1rem; }
        almari-spatial-portal button {
          font: inherit; font-size: 0.9rem; border-radius: 999px; padding: 0.5rem 1.2rem;
          border: 1px solid #2a221b; background: transparent; color: #2a221b; cursor: pointer;
        }
        almari-spatial-portal button:hover { background: rgba(42, 34, 27, 0.08); }
        almari-spatial-portal button[disabled] { opacity: 0.4; cursor: not-allowed; }
        almari-spatial-portal .note { margin-top: 0.75rem; opacity: 0.6; font-size: 0.82rem; line-height: 1.45; }
      </style>
      <header>
        <h3>Walk through your closet</h3>
        <span class="badge">${arSupported ? "AR ready" : inlineSupported ? "Inline preview" : "2D fallback"}</span>
      </header>
      <p class="lede">Nau garments arrange themselves in a soft ring. Pinch to lift one. The price per wear floats above. Available on Vision Pro, Quest, Snap Spectacles, and any WebXR-capable browser.</p>
      <div class="stage" role="img" aria-label="Almari spatial closet preview"></div>
      <div class="actions">
        <button data-action="enter" ${arSupported ? "" : "disabled"}>${arSupported ? "Enter AR" : "AR not supported"}</button>
        <button data-action="inline">Inline preview</button>
      </div>
      <p class="note">Pro + Spatial tier ₹1,499/yr unlocks WebXR + anchor persistence. Camera frames stay on device under DPDP consent.</p>
    `;

    const stage = this.querySelector(".stage") as HTMLElement;
    this.querySelector('[data-action="enter"]')?.addEventListener("click", () => void this.enter(stage));
    this.querySelector('[data-action="inline"]')?.addEventListener("click", () => void this.inline(stage));
  }

  private async enter(stage: HTMLElement): Promise<void> {
    try {
      if (!await this.cameraConsent()) return;
      const xr = navigator.xr;
      if (!xr) return;
      const session = await xr.requestSession("immersive-ar", {
        requiredFeatures: ["local-floor"],
        optionalFeatures: ["hand-tracking", "hit-test", "anchors"],
      });
      this.currentSession = session as XRSession;
      const THREE = await loadThree();
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
      renderer.xr.enabled = true;
      stage.replaceChildren(renderer.domElement);
      await renderer.xr.setSession(this.currentSession);
      const garments = await readGarments();
      const built = await buildClosetScene(THREE, garments);
      void playAmbientAudio();
      renderer.setAnimationLoop((t: number) => {
        built.tick(t);
        renderer.render(built.scene, built.camera);
      });
      this.currentSession.addEventListener("end", () => {
        renderer.setAnimationLoop(null);
        renderer.dispose();
        this.currentSession = null;
        void this.render();
      });
    } catch (err) {
      console.warn("[almari-spatial] enter failed", err);
    }
  }

  private async inline(stage: HTMLElement): Promise<void> {
    try {
      const THREE = await loadThree();
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
      const w = stage.clientWidth || 640;
      const h = stage.clientHeight || 360;
      renderer.setSize(w, h);
      stage.replaceChildren(renderer.domElement);
      const garments = await readGarments();
      const built = await buildClosetScene(THREE, garments);
      built.camera.aspect = w / h;
      built.camera.updateProjectionMatrix();
      renderer.setAnimationLoop((t: number) => {
        built.tick(t);
        renderer.render(built.scene, built.camera);
      });
    } catch (err) {
      console.warn("[almari-spatial] inline failed", err);
    }
  }

  private async cameraConsent(): Promise<boolean> {
    return window.confirm("Enable camera for AR closet view? Frames never leave this device.");
  }
}

if (!customElements.get("almari-spatial-portal")) {
  customElements.define("almari-spatial-portal", AlmariSpatialPortal);
}
